import {useContext} from 'react';

import {useTranslation} from 'react-i18next';

import {Box, Chip, Grid, List, ListItem, Typography} from '@mui/material';

import {LanguageContext} from '../../../../../../contexts/LanguageContext/LanguageContext';
import {orangeColors} from '../../../../../../layouts/theme/designSystem';
import {safelyUseLanguageProfile} from '../../../../../../utils/safelyUseLanguageProfile';
import {JobPosting} from '../../types/JobPostingsTypes';

interface JobPostingAccordionDetailsBodyProps {
  jobPosting: JobPosting;
}

export default function JobPostingAccordionDetailsBody({jobPosting}: JobPostingAccordionDetailsBodyProps) {
  const {t} = useTranslation();
  const currentLanguage = useContext(LanguageContext);

  const renderListItems = (listItems: string[] | undefined) => {
    if (listItems && listItems.length > 0) {
      return listItems.map((item, index) => {
        if (!item.length) {
          return null;
        }

        if (item.length === 1) {
          return <Typography key={index} variant='body1' dangerouslySetInnerHTML={{__html: item}} />;
        }

        return (
          <List
            key={index}
            sx={{
              listStyleType: 'disc',
              pl: 5,
              padding: '0px 0px 0px 34px',
            }}
          >
            <ListItem
              sx={{
                display: 'list-item',
              }}
            >
              <Typography variant='body1' dangerouslySetInnerHTML={{__html: item}} />
            </ListItem>
          </List>
        );
      });
    }
  };

  const renderSkills = (jobPosting: JobPosting) => {
    if (jobPosting.skills && jobPosting.skills.length > 0) {
      return jobPosting.skills.map((skill, index) => {
        const {name} = skill;
        return (
          <Chip
            key={index}
            label={<Typography variant='body1'>{safelyUseLanguageProfile(currentLanguage, name) || ''}</Typography>}
            sx={{
              borderRadius: '2px',
              backgroundColor: orangeColors[5],
              padding: '24px',
              width: 'fit-content',
            }}
          />
        );
      });
    }
  };

  return (
    <Grid container marginTop={4} columns={12}>
      <Grid item xs={12}>
        <Typography variant='h2' gutterBottom>
          {t('companyJobPostings.jobPostingsAccordion.accordionDetailsBody.aboutThePosition')}
        </Typography>
        <Typography variant='h3' gutterBottom>
          {t('companyJobPostings.newJobDialog.formLabels.responsibilities')}
        </Typography>
      </Grid>
      <Grid item xs={12} md={8}>
        {renderListItems(jobPosting.responsibilities)}
      </Grid>
      <Grid item xs={12}>
        <Typography
          variant='h3'
          sx={{
            margin: '32px 0 16px 0',
          }}
        >
          {t('companyJobPostings.newJobDialog.formLabels.requirements')}
        </Typography>
      </Grid>
      <Grid item xs={12} md={8}>
        {renderListItems(jobPosting.requirements)}
      </Grid>
      <Grid item xs={12}>
        <Typography
          variant='h3'
          sx={{
            margin: '32px 0 16px 0',
          }}
        >
          {t('companyJobPostings.newJobDialog.formLabels.skills')}
        </Typography>
        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: '0.5rem', margin: '18px'}}>{renderSkills(jobPosting)}</Box>
      </Grid>
    </Grid>
  );
}
